import { useMemo, useState } from 'react';
import { ErrorBanner, Loading } from '../../components/ui';
import { api } from '../../lib/api';
import { useResource } from '../../lib/hooks';

/**
 * DD Request List — the formal buyer-side request list, as issued.
 *
 * Shares the A–I taxonomy with the Data Room folder index but is a different
 * document: this one carries the request wording, its status, the data room
 * reference and each party's response.
 */
const STATUSES = [
  { value: 'not_started', label: 'Not started' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'complete', label: 'Complete' },
  { value: 'na', label: 'N/A' },
] as const;

/* Progress runs navy (untouched) -> crimson-tinted (in flight) -> navy again
   (done). The brand has no green, so "complete" is stated by the badge word
   rather than by a colour nobody agreed on. */
const STATUS_BADGE: Record<string, string> = {
  not_started: 'badge-neutral',
  in_progress: 'badge-warning',
  complete: 'badge-good',
  na: 'badge-neutral',
};

interface DdRequest {
  MaDdRequestId: string;
  CategoryCode: string;
  CategoryName: string;
  Code: string;
  Num: number;
  Request: string;
  Note: string;
  Status: string;
  DataRoomRef: string;
  ResponseAmt: string;
  ResponseItv: string;
  SortOrder: number;
}

export function MaDdRequests() {
  const list = useResource<DdRequest[]>('/ma/dd-requests');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const rows = useMemo(() => list.data ?? [], [list.data]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (status === '' || r.Status === status) &&
        (q === '' || r.Request.toLowerCase().includes(q) || r.Code.toLowerCase().includes(q)),
    );
  }, [rows, search, status]);

  const categories = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of rows) if (!seen.has(r.CategoryCode)) seen.set(r.CategoryCode, r.CategoryName);
    return [...seen.entries()].map(([code, name]) => ({ code, name }));
  }, [rows]);

  const save = async (id: string, patch: Record<string, unknown>) => {
    setProblem(null);
    try {
      await api.put(`/ma/dd-requests/${id}`, patch);
      list.reload();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not save');
    }
  };

  if (list.loading) return <Loading label="Loading the request list" />;
  if (list.error) return <ErrorBanner message={list.error} />;

  const complete = rows.filter((r) => r.Status === 'complete').length;
  const inProgress = rows.filter((r) => r.Status === 'in_progress').length;

  return (
    <div className="stack">
      {problem && <ErrorBanner message={problem} />}

      <div className="card">
        <div className="card-body tight">
          <div className="row wrap">
            <input
              className="input"
              style={{ maxWidth: 320 }}
              placeholder="Search the requests"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="select"
              style={{ maxWidth: 180 }}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">Any status</option>
              {STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
            <span className="grow" />
            <span className="xs muted">
              <span className="mono">{complete}</span> complete · <span className="mono">{inProgress}</span> in
              progress · <span className="mono">{rows.length}</span> total
            </span>
          </div>
        </div>
      </div>

      {categories.map((cat) => {
        const catRows = visible.filter((r) => r.CategoryCode === cat.code);
        if (catRows.length === 0) return null;
        const all = rows.filter((r) => r.CategoryCode === cat.code);
        const done = all.filter((r) => r.Status === 'complete').length;

        return (
          <div className="card" key={cat.code}>
            <div className="card-head">
              <div className="row-between">
                <h2>
                  <span className="mono subtle">{cat.code}.</span> {cat.name}
                </h2>
                <span className="xs muted">
                  <span className="mono">{done}</span>/<span className="mono">{all.length}</span> complete
                </span>
              </div>
            </div>

            <div className="card-body">
              <div className="stack" style={{ gap: 'var(--space-2)' }}>
                {catRows.map((r) => (
                  <div className="ma-dd" key={r.MaDdRequestId}>
                    <button
                      type="button"
                      className="ma-dd-head"
                      onClick={() => setOpen(open === r.MaDdRequestId ? null : r.MaDdRequestId)}
                      aria-expanded={open === r.MaDdRequestId}
                    >
                      <span className="mono xs subtle" style={{ minWidth: 46 }}>
                        {r.Code}
                      </span>
                      <span className="grow ma-dd-text">{r.Request}</span>
                      <span className={`badge ${STATUS_BADGE[r.Status] ?? 'badge-neutral'}`}>
                        {STATUSES.find((s) => s.value === r.Status)?.label ?? r.Status}
                      </span>
                      <span className="xs subtle" aria-hidden="true">
                        {open === r.MaDdRequestId ? '▾' : '▸'}
                      </span>
                    </button>

                    {open === r.MaDdRequestId && (
                      <div className="ma-dd-body">
                        {r.Note && <p className="xs muted">{r.Note}</p>}

                        <div className="form-grid">
                          <div className="field">
                            <label>Status</label>
                            <select
                              className="select"
                              value={r.Status}
                              onChange={(e) => void save(r.MaDdRequestId, { status: e.target.value })}
                            >
                              {STATUSES.map((s) => (
                                <option key={s.value} value={s.value}>
                                  {s.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="field">
                            <label>Data room reference</label>
                            <input
                              className="input"
                              defaultValue={r.DataRoomRef}
                              placeholder="e.g. B.4"
                              onBlur={(e) => {
                                if (e.target.value !== r.DataRoomRef)
                                  void save(r.MaDdRequestId, { dataRoomRef: e.target.value });
                              }}
                            />
                          </div>
                        </div>

                        <div className="field">
                          <label>Seller response</label>
                          <textarea
                            className="input"
                            rows={2}
                            defaultValue={r.ResponseAmt}
                            onBlur={(e) => {
                              if (e.target.value !== r.ResponseAmt)
                                void save(r.MaDdRequestId, { responseAmt: e.target.value });
                            }}
                          />
                        </div>

                        <div className="field">
                          <label>Purchaser response</label>
                          <textarea
                            className="input"
                            rows={2}
                            defaultValue={r.ResponseItv}
                            onBlur={(e) => {
                              if (e.target.value !== r.ResponseItv)
                                void save(r.MaDdRequestId, { responseItv: e.target.value });
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
